import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { CalendarClock, ChevronRight, ClipboardList, Gauge } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Card } from '@repo/ui/src/components/ui/card';
import { Checkbox } from '@repo/ui/src/components/ui/checkbox';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import { DropdownMenuItem } from '@repo/ui/src/components/ui/dropdown-menu';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
} from '@repo/ui/src/components/ui/item';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { cn } from '@repo/ui/src/lib/utils';
import { toast } from 'sonner';
import type { ReopenPreviewRow } from '@repo/shared-types';
import { useInvoicingPreview, useRunInvoicing, useReopenMonth, useReopenPreview, useReopenHistory } from '../../hooks/useInvoicing';
import { useCan } from '../../hooks/useCan';
import { useMultiSelect } from '../../hooks/useMultiSelect';
import { useServerNow } from '../../hooks/useServerNow';
import { useOrgSettings, useUpdateOrgSettings } from '../../hooks/useOrgSettings';
import { currentMonth, prevMonth } from '../../lib/sessionTime';
import { nextDueDateForDay } from '../../lib/dueDatePrefill';
import { staleOpenRows, type StaleOpenMonthRow } from '../../lib/staleOpen';
import { monthFmt, owedMonthFmt } from '../../lib/attentionItems';
import { InvoiceStatusBadge } from '../../lib/invoiceStatus';
import { useUpgradeDialogStore } from '../../stores/upgradeDialogStore';
import { useReconcileStatus } from '../../hooks/useReconcileStatus';
import { PageHeader, pageHeaderActionClass } from '../../components/PageHeader';
import { BusyButton } from '../../components/BusyButton';
import { SelectionBar } from '../../components/SelectionBar';
import { AttendanceGrid } from '../../components/AttendanceGrid';
import { MonthPicker } from '../../components/pickers/MonthPicker';
import { DatePicker } from '../../components/pickers/DatePicker';

// Default month = the current calendar month (UTC-safe) from `now` (the
// browser clock or the possibly-simulated server clock): we prepay the
// current/upcoming month while reconciling (bù trừ) the previous one — so the
// attendance-review grid below shows prevMonth(month).
const vnd = (n: number) => n.toLocaleString('vi-VN') + 'đ';

// Group a payer's invoice lines per student so a parent with several children
// reads clearly (each child = a labelled sub-block with its own subtotal).
type BillLine = { enrollmentId: string; studentName: string; subjectName: string; kind: string; qty: number; rate: number; amount: number };
function groupByStudent(lines: BillLine[]) {
  const map = new Map<string, { studentName: string; subtotal: number; lines: BillLine[] }>();
  for (const ln of lines) {
    const g = map.get(ln.studentName) ?? { studentName: ln.studentName, subtotal: 0, lines: [] };
    g.subtotal += ln.amount;
    g.lines.push(ln);
    map.set(ln.studentName, g);
  }
  return [...map.values()];
}

/** "A", "A, B", or "A, B và 2 người khác" — the join is copy, so it happens
 *  here and not in lib/staleOpen. */
function useStaleNames() {
  const { t } = useTranslation('invoicing');
  return (row: StaleOpenMonthRow) =>
    row.namesExtra > 0
      ? t('closeMonth.guidance.staleNamesMore', { names: row.namesShown.join(', '), count: row.namesExtra })
      : row.namesShown.join(', ');
}

/** The 02 row anatomy, once: ochre icon circle + title + consequence + one
 *  action. `--spacing` is 0.2rem, so the circle and the wrapped-action indent
 *  are pinned in px; the action drops to its own line under the text below
 *  640 rather than squeezing the sentence. */
function GuidanceRow({
  icon: Icon, title, description, action, separator = false, children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
  separator?: boolean;
  children?: ReactNode;
}) {
  return (
    <div role="listitem">
      {separator && <ItemSeparator />}
      <Item size="sm" className="items-start">
        <ItemMedia variant="icon" className="size-[28.8px] shrink-0 rounded-full border-0 bg-attention-tint text-attention-foreground">
          <Icon className="size-3.5" strokeWidth={1.9} />
        </ItemMedia>
        <ItemContent className="min-w-0">
          <ItemTitle className="text-sm font-semibold">{title}</ItemTitle>
          <ItemDescription className="text-xs line-clamp-none">{description}</ItemDescription>
          {children}
        </ItemContent>
        {action && <ItemActions className="max-sm:basis-full max-sm:pl-[36.8px] sm:ml-auto">{action}</ItemActions>}
      </Item>
    </div>
  );
}

export function CloseMonthPage() {
  const { t } = useTranslation('invoicing');
  const navigate = useNavigate();
  const now = useServerNow();
  const [month, setMonth] = useState(() => currentMonth(now));
  // THI-170: opens the partial-reopen picker (fed by GET /reopen-preview)
  // instead of a plain "reopen the whole month" confirm — see ReopenPickerDialog below.
  const [reopenPickerOpen, setReopenPickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // THI-259: the reopen history is one slice of the org log; offer the whole thing.
  const canSeeOrgLog = useCan('manage:settings');
  const [expandedStale, setExpandedStale] = useState<string | null>(null);
  const { data: preview, isLoading } = useInvoicingPreview(month);
  const run = useRunInvoicing();
  const { data: reconcileStatus } = useReconcileStatus();
  const openUpgradeDialog = useUpgradeDialogStore((s) => s.open);
  const { data: orgSettings } = useOrgSettings();
  const updateOrgSettings = useUpdateOrgSettings();

  // ── Generate confirm dialog (THI-100) — see docs/superpowers/specs/
  // 2026-08-19-due-date-control-design.md §2. `dueDateValue` is the
  // `YYYY-MM-DD` the native date input holds; empty means "no override, the
  // +7-days rule stands" — never coerced to a placeholder date.
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [dueDateExpanded, setDueDateExpanded] = useState(false);
  const [dueDateValue, setDueDateValue] = useState('');
  const [dueDateError, setDueDateError] = useState<string | null>(null);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  // VN wall-clock "today" as YYYY-MM-DD — same UTC-getter convention as
  // sessionTime.ts's currentMonth/prevMonth (both already read `now` this
  // way above). Plain string comparison against it is safe: both sides are
  // always YYYY-MM-DD.
  const todayKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;

  const payers = preview?.payers ?? [];
  // Enrollments whose earlier month was never closed — closing THIS month
  // will not reconcile them, and once the calendar rolls over nothing else
  // on the dashboard mentions them either. See docs/superpowers/specs/
  // 2026-08-11-stale-open-period-warning-design.md.
  const staleOpen = preview?.staleOpen ?? [];

  // Grouped for display, oldest month first (staleOpenRows sorts). Each row's
  // own action targets `row.closeLabel` and is hidden only when that IS the
  // month currently being viewed — no separate "is anything stale" check is
  // needed since `staleRows` is only ever rendered when non-empty.
  const { rows: staleRows, hiddenMonths: staleHiddenMonths } = staleOpenRows(staleOpen);
  const pendingPrev = preview?.pendingCount ?? 0;
  const prev = prevMonth(month);
  const staleNames = useStaleNames();

  // Excluded rather than selected: the default is everyone, and the payer list
  // arrives asynchronously and changes with the month — tracking the exclusions
  // keeps "all ticked" true at every moment without an effect to re-sync.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  // The month the stale-month warning sent them away from, if that is how
  // they got here. Only set by the warning's button — see goToMonth.
  const [jumpedFrom, setJumpedFrom] = useState<string | null>(null);

  const selectedPayers = payers.filter((p) => !excluded.has(p.clientId));
  const selectedCount = selectedPayers.length;
  const selectedTotal = selectedPayers.reduce((s, p) => s + p.total, 0);
  const allSelected = payers.length > 0 && selectedCount === payers.length;

  // Click-anywhere + shift-range (THI-180), driven through the SAME
  // "excluded" state above rather than a parallel selectedIds — the hook is
  // controlled, so it can toggle/range a derived selected-set (payers minus
  // exclusions) and translate the result straight back into an exclusion
  // set, keeping the async-payer-list-without-an-effect property intact.
  const payerIds = payers.map((p) => p.clientId);
  const selectedPayerIds = new Set(selectedPayers.map((p) => p.clientId));
  const multiPayers = useMultiSelect(payerIds, selectedPayerIds, (next) =>
    setExcluded(new Set(payers.filter((p) => !next.has(p.clientId)).map((p) => p.clientId))),
  );

  const toggleAll = () => (allSelected ? multiPayers.clear() : multiPayers.selectAll());

  const canGenerate = selectedCount > 0;
  // Non-blocking heads-up (does NOT gate "chốt"): the payer count is on track
  // to exceed this org's remaining auto-reconcile quota for the month, so the
  // excess invoices will need manual reconciliation once paid.
  const quotaShort =
    !!preview &&
    !!reconcileStatus?.linked &&
    !reconcileStatus.unlimited &&
    selectedCount > reconcileStatus.remaining;

  // The card renders only when it has something to say — an empty "Trước khi
  // chốt" heading is worse than no card at all.
  const guidanceCount =
    (pendingPrev > 0 ? 1 : 0) + staleRows.length + (staleHiddenMonths > 0 ? 1 : 0) + (quotaShort ? 1 : 0);

  // Opens the confirm dialog (first this flow has had — design spec §2)
  // instead of running the mutation directly. The optional due-date section
  // is collapsed by default; when the org has an `invoiceDueDay`, it opens
  // pre-expanded and prefilled with the soonest today-or-future occurrence of
  // that day (lib/dueDatePrefill.ts) — and the save-default checkbox starts
  // checked, since re-saving exactly the stored day changes nothing.
  const openGenerateDialog = () => {
    const defaultDay = orgSettings?.invoiceDueDay ?? null;
    if (defaultDay) {
      setDueDateValue(nextDueDateForDay(defaultDay, now));
      setDueDateExpanded(true);
      setSaveAsDefault(true);
    } else {
      setDueDateValue('');
      setDueDateExpanded(false);
      setSaveAsDefault(false);
    }
    setDueDateError(null);
    setConfirmGenerate(true);
  };

  const onConfirmGenerate = () => {
    // Empty override (section collapsed, or expanded but cleared) → the
    // +7-days rule stands, unchanged — `dueDate` is omitted from the mutation
    // payload entirely (useRunInvoicing only sends it when defined), so the
    // common case sends exactly the payload it always sent.
    let dueDate: string | undefined;
    if (dueDateExpanded && dueDateValue.trim() !== '') {
      if (dueDateValue < todayKey) {
        setDueDateError(t('closeMonth.confirmDialog.dueDatePast'));
        return;
      }
      dueDate = dueDateValue;
    }

    run.mutate(
      { month, clientIds: allSelected ? undefined : selectedPayers.map((p) => p.clientId), dueDate },
      {
        onSuccess: (res) => {
          toast.success(t('closeMonth.done', { count: res.created.length }));
          setConfirmGenerate(false);
          // Save-as-default only fires when it would actually change the
          // stored setting — ticking the box while re-confirming the day
          // already on file is a no-op, not a redundant write. Silent on
          // success: the run's own toast above is the feedback for this whole
          // action; useUpdateOrgSettings still toasts on error.
          if (dueDate && saveAsDefault) {
            const chosenDay = Number(dueDate.slice(-2));
            if (chosenDay !== orgSettings?.invoiceDueDay) {
              updateOrgSettings.mutate({ invoiceDueDay: chosenDay });
            }
          }
          navigate('/invoices');
        },
      },
    );
  };

  // Changing the month must always clear the exclusions: the payer list is
  // wholly different, and an id unticked for September means nothing in August.
  // `jumpedFrom` records the month the stale-month warning sent them away from,
  // and only that: an empty month is overwhelmingly the ordinary "you finished
  // closing it" case, and warning every teacher about reopening a period would
  // be noise. It is the arrival path, not the emptiness, that makes the hint
  // worth showing — so a manual month change clears it.
  const goToMonth = (next: string, from?: string) => {
    setMonth(next);
    setExcluded(new Set());
    setJumpedFrom(from ?? null);
  };

  // A day > 28 in the currently-picked date can never be saved as the org
  // default (Zod caps `invoiceDueDay` at 1-28) — drives the save-default
  // checkbox's disabled state and hint below. The onChange handler on the
  // date input also force-unchecks `saveAsDefault` the moment such a date is
  // picked, so this only needs to *reflect* that state here, not set it.
  const dueDateDayTooHighForDefault = dueDateValue !== '' && Number(dueDateValue.slice(-2)) > 28;

  // Scroll to the reconciliation grid and put the caret on its first un-marked
  // cell, so the action lands the teacher ON the work rather than near it.
  // `preventScroll` because the smooth scroll above is already in flight — a
  // focus-driven jump would cancel it and land hard.
  const focusFirstPendingCell = () => {
    const grid = document.getElementById('attendance-grid');
    if (!grid) return;
    grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    grid.querySelector<HTMLElement>('[data-pending-cell]')?.focus({ preventScroll: true });
  };

  return (
    <div className={cn('p-8 max-w-4xl mx-auto w-full', selectedCount > 0 && 'pb-40 md:pb-24')}>
      <PageHeader
        size="md"
        back={{ label: t('closeMonth.backToInvoices'), to: '/invoices' }}
        title={t('closeMonth.title')}
        subtitle={t('closeMonth.prepayHint')}
        extra={<MonthPicker value={month} onChange={(next) => goToMonth(next)} className={pageHeaderActionClass} />}
        overflow={
          <DropdownMenuItem onSelect={() => setHistoryOpen(true)}>
            {t('closeMonth.reopenHistoryTitle')}
          </DropdownMenuItem>
        }
        actions={[
          {
            key: 'reopen',
            label: t('closeMonth.reopen'),
            destructive: true,
            disabled: isLoading,
            onClick: () => setReopenPickerOpen(true),
          },
        ]}
      />

      {guidanceCount > 0 && (
        <Card className="mb-6 overflow-hidden rounded-xl border-surface-200 p-0">
          <div className="flex items-center gap-2 border-b border-surface-200 px-4 py-3">
            <h2 className="font-display text-[15px] font-semibold text-surface-900">{t('closeMonth.guidance.title')}</h2>
            <span
              role="img"
              aria-label={t('closeMonth.guidance.countLabel', { count: guidanceCount })}
              className="tabular inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-attention-tint px-1.5 text-[11px] font-semibold text-attention-foreground"
            >
              {guidanceCount}
            </span>
          </div>
          <ItemGroup>
            {pendingPrev > 0 && (
              <GuidanceRow
                icon={ClipboardList}
                title={t('closeMonth.guidance.pendingTitle', { count: pendingPrev, prev: owedMonthFmt(prev, month) })}
                description={t('closeMonth.guidance.pendingDesc', { count: pendingPrev })}
                action={
                  <Button type="button" size="sm" variant="outline" onClick={focusFirstPendingCell}>
                    {t('closeMonth.guidance.pendingAction')}
                  </Button>
                }
              />
            )}

            {staleRows.map((row, i) => (
              <GuidanceRow
                key={row.closeLabel}
                separator={pendingPrev > 0 || i > 0}
                icon={CalendarClock}
                title={t('closeMonth.guidance.staleTitle', {
                  month: owedMonthFmt(row.closeLabel, month),
                  names: staleNames(row),
                })}
                description={t('closeMonth.guidance.staleDesc', {
                  month: owedMonthFmt(row.closeLabel, month),
                  current: monthFmt(month),
                })}
                action={
                  row.closeLabel !== month ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-attention/40 text-attention-foreground hover:bg-attention/15"
                      onClick={() => goToMonth(row.closeLabel, month)}
                    >
                      {t('closeMonth.staleOpenGoto', { month: owedMonthFmt(row.closeLabel, month) })}
                    </Button>
                  ) : null
                }
              >
                <button
                  type="button"
                  aria-expanded={expandedStale === row.closeLabel}
                  onClick={() => setExpandedStale((v) => (v === row.closeLabel ? null : row.closeLabel))}
                  className="mt-1 flex items-center gap-1 text-xs font-medium text-surface-600 hover:text-surface-900"
                >
                  <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expandedStale === row.closeLabel && 'rotate-90')} />
                  {t('closeMonth.guidance.staleDetails')}
                </button>
                {expandedStale === row.closeLabel && (
                  <>
                    {/* Capped and scrollable: the dominant real trigger is an
                        org-wide skipped month, which can mean every enrollment
                        in the org — this fold must never be able to push the
                        payer list, the real work of the screen, off-screen. */}
                    <ul className="mt-1.5 max-h-40 space-y-0.5 overflow-y-auto">
                      {row.entries.slice(0, 8).map((s, j) => (
                        <li key={j} className="min-w-0 truncate text-xs text-muted-foreground">
                          {s.reason === 'UNBILLED'
                            ? t('closeMonth.staleOpenItemUnbilled', { student: s.studentName, client: s.clientName, count: s.sessionCount, month: owedMonthFmt(s.closeLabel, month) })
                            : t('closeMonth.staleOpenItem', { student: s.studentName, client: s.clientName, month: owedMonthFmt(s.closeLabel, month) })}
                        </li>
                      ))}
                    </ul>
                    {row.entries.length > 8 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('closeMonth.staleOpenMore', { count: row.entries.length - 8 })}
                      </p>
                    )}
                  </>
                )}
              </GuidanceRow>
            ))}

            {staleHiddenMonths > 0 && (
              <div role="listitem" className="px-4 pb-3 text-xs text-muted-foreground">
                {t('closeMonth.guidance.staleMonthsMore', { count: staleHiddenMonths })}
              </div>
            )}

            {quotaShort && (
              <GuidanceRow
                separator={pendingPrev > 0 || staleRows.length > 0}
                icon={Gauge}
                title={t('closeMonth.guidance.quotaTitle', { remaining: reconcileStatus!.remaining })}
                description={t('closeMonth.guidance.quotaDesc', { payerCount: selectedCount })}
                action={
                  <Button type="button" size="sm" variant="outline" onClick={() => openUpgradeDialog()}>
                    {t('closeMonth.guidance.quotaAction')}
                  </Button>
                }
              />
            )}
          </ItemGroup>
        </Card>
      )}

      <AttendanceGrid
        month={prev}
        title={t('closeMonth.gridTitle', { month: owedMonthFmt(prev, month) })}
        // The hint used to be hard-coded inside the grid. It is close-month
        // copy ("bấm vào để điểm danh nhanh" is advice about closing), so it
        // stays in the `invoicing` namespace and travels as a prop.
        hint={t('closeMonth.gridHint')}
      />

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="border rounded-md bg-card p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-20" />
              </div>
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
            </div>
          ))}
        </div>
      ) : payers.length === 0 ? (
        <p className="text-muted-foreground py-12 text-center border rounded-md bg-card">
          {jumpedFrom ? t('closeMonth.emptyMaybeStale', { from: jumpedFrom }) : t('closeMonth.empty')}
        </p>
      ) : (
        <div className={cn('space-y-4', multiPayers.containerProps.className)}>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-[15px] font-semibold text-surface-900">{t('closeMonth.willCreate')}</h2>
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all-payers"
                checked={allSelected ? true : selectedCount === 0 ? false : 'indeterminate'}
                onCheckedChange={toggleAll}
              />
              <label htmlFor="select-all-payers" className="text-sm text-muted-foreground cursor-pointer">
                {t('closeMonth.selectAll')}
              </label>
            </div>
          </div>

          {payers.map((p) => (
            <div
              key={p.clientId}
              className={cn('rounded-xl border border-surface-200 bg-card p-4', excluded.has(p.clientId) && 'opacity-55')}
              {...multiPayers.getRowProps(p.clientId)}
            >
              <div className="flex items-center justify-between mb-2 gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {/* stopPropagation: the card itself now toggles on click
                      (THI-180) while selection mode is active — without
                      this, clicking the checkbox would flip the row twice. */}
                  <span onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={multiPayers.isSelected(p.clientId)}
                      onCheckedChange={() => multiPayers.toggle(p.clientId)}
                      aria-label={t('closeMonth.selectPayer', { name: p.clientName })}
                    />
                  </span>
                  <span className="font-semibold truncate">{p.clientName}</span>
                </div>
                <span className="font-semibold tabular-nums shrink-0">{vnd(p.total)}</span>
              </div>
              <div className="space-y-2.5">
                {groupByStudent(p.lines).map((g) => (
                  <div key={g.studentName}>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-surface-800">{g.studentName}</span>
                      <span className="font-medium tabular-nums">{vnd(g.subtotal)}</span>
                    </div>
                    <div className="mt-0.5 space-y-0.5 border-l pl-3">
                      {g.lines.map((ln, i) => (
                        <div key={`${ln.enrollmentId}-${ln.kind}-${i}`} className="flex justify-between text-xs text-muted-foreground">
                          <span>
                            {ln.kind === 'PREPAY'
                              ? `${ln.subjectName}: ${ln.qty} ${t('closeMonth.sessions')} × ${vnd(ln.rate)}`
                              : ln.kind === 'FLAT'
                                ? `${ln.subjectName}: ${t('closeMonth.flatFee')}`
                                : `${ln.subjectName} · ${t('closeMonth.adjust')}: ${ln.qty > 0 ? '+' : ''}${ln.qty} ${t('closeMonth.sessions')}`}
                          </span>
                          <span className={cn('tabular-nums', ln.kind === 'ADJUST' && ln.amount < 0 && 'text-brand-700')}>
                            {ln.kind === 'ADJUST' ? (ln.amount < 0 ? `−${vnd(-ln.amount)}` : `+${vnd(ln.amount)}`) : vnd(ln.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {p.pendingCount > 0 && (
                <Badge variant="warning" className="mt-2.5 font-normal" title={t('closeMonth.pending', { count: p.pendingCount })}>
                  {t('closeMonth.pendingChip')}
                  <span className="sr-only"> {t('closeMonth.pending', { count: p.pendingCount })}</span>
                </Badge>
              )}
            </div>
          ))}

          {selectedCount === 0 && (
            <p className="pt-2 text-sm text-muted-foreground">{t('closeMonth.noneSelected')}</p>
          )}
        </div>
      )}

      <SelectionBar
        id="close-month"
        ariaLabel={t('closeMonth.footerAriaLabel')}
        active={selectedCount > 0}
        summary={
          <div className="min-w-0">
            <p className="font-display tabular text-base font-semibold text-surface-900">
              {t('closeMonth.summary', { payers: selectedCount, amount: vnd(selectedTotal) })}
            </p>
            <p className="text-xs font-normal text-muted-foreground">{t('closeMonth.footerNote')}</p>
          </div>
        }
        actions={
          <BusyButton
            type="button"
            onClick={openGenerateDialog}
            busy={run.isPending}
            busyLabel={t('closeMonth.generating')}
            disabled={!canGenerate}
          >
            {t('closeMonth.generate', { count: selectedCount })}
          </BusyButton>
        }
      />

      <ReopenPickerDialog
        open={reopenPickerOpen}
        month={month}
        onOpenChange={setReopenPickerOpen}
        onReopened={() => {
          // The reopened month's payers become eligible again — the previous
          // generate-invoices exclusion set was computed against the payer
          // list before this refill and would otherwise silently keep last
          // close's unticked payers unticked.
          setExcluded(new Set());
        }}
      />

      <Dialog open={confirmGenerate} onOpenChange={(o) => !o && setConfirmGenerate(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('closeMonth.confirmDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('closeMonth.confirmDialog.description', { count: selectedCount, amount: vnd(selectedTotal) })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setDueDateExpanded((v) => !v)}
              aria-expanded={dueDateExpanded}
              className="flex items-center gap-1 text-sm font-medium text-surface-700"
            >
              <ChevronRight className={cn('h-4 w-4 transition-transform', dueDateExpanded && 'rotate-90')} />
              {t('closeMonth.confirmDialog.dueDateToggle')}
            </button>
            {dueDateExpanded && (
              <div className="space-y-2 pl-5">
                <DatePicker
                  id="close-month-due-date"
                  value={dueDateValue}
                  min={todayKey}
                  aria-label={t('closeMonth.confirmDialog.dueDateLabel')}
                  onChange={(next) => {
                    setDueDateValue(next);
                    setDueDateError(null);
                    // A day > 28 can never be saved as the org default (Zod
                    // caps `invoiceDueDay` at 28) — force the checkbox off
                    // the moment such a date is picked so a stale `true`
                    // from an earlier, valid selection can't sneak through
                    // to onConfirmGenerate. The run itself is unaffected:
                    // `dueDate` still carries this exact date either way.
                    if (next && Number(next.slice(-2)) > 28) {
                      setSaveAsDefault(false);
                    }
                  }}
                  className="w-full"
                />
                {dueDateError && <p className="text-xs text-destructive">{dueDateError}</p>}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="save-due-date-default"
                    checked={saveAsDefault}
                    disabled={dueDateDayTooHighForDefault}
                    onCheckedChange={(v) => setSaveAsDefault(v === true)}
                  />
                  <label htmlFor="save-due-date-default" className="text-sm text-muted-foreground cursor-pointer">
                    {t('closeMonth.confirmDialog.saveDefault')}
                  </label>
                </div>
                {dueDateDayTooHighForDefault && (
                  <p className="text-xs text-muted-foreground">
                    {t('closeMonth.confirmDialog.saveDefaultDayTooHigh')}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmGenerate(false)} disabled={run.isPending}>
              {t('closeMonth.cancel')}
            </Button>
            <BusyButton
              type="button"
              onClick={onConfirmGenerate}
              busy={run.isPending}
              busyLabel={t('closeMonth.confirmDialog.confirming')}
            >
              {t('closeMonth.confirmDialog.confirm')}
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('closeMonth.reopenHistoryTitle')}</DialogTitle>
            <DialogDescription>{t('closeMonth.reopenHistoryDesc')}</DialogDescription>
          </DialogHeader>
          <ReopenHistoryList />
          <DialogFooter className="sm:justify-between">
            {canSeeOrgLog ? (
              <Link
                to="/settings/audit?group=money"
                onClick={() => setHistoryOpen(false)}
                className="inline-flex min-h-[38.4px] items-center gap-[3.2px] text-[13px] font-medium text-brand-700 hover:text-brand-800"
              >
                {t('closeMonth.reopenHistoryAll')}
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
            ) : <span />}
            <Button type="button" variant="ghost" onClick={() => setHistoryOpen(false)}>
              {t('close', { ns: 'common' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// I6 surface: the audit trail of month reopens (a money-moving action). Rare,
// so it lives behind the header ⋮ rather than occupying the page (design §1).
// Mounted only while the dialog is open, so the query does not run on every
// visit to this screen.
function ReopenHistoryList() {
  const { t } = useTranslation('invoicing');
  const { data: entries } = useReopenHistory();
  if (!entries || entries.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('closeMonth.reopenHistoryEmpty')}</p>;
  }
  return (
    <ul className="max-h-80 space-y-1 overflow-y-auto">
      {entries.map((e) => {
        const meta = (e.metadata ?? {}) as { year?: number; month?: number; count?: number };
        const label = meta.year && meta.month ? `${String(meta.month).padStart(2, '0')}/${meta.year}` : e.entityId;
        return (
          <li key={e.id} className="text-xs text-muted-foreground flex items-center justify-between gap-3 border rounded-md bg-card px-3 py-1.5">
            <span className="min-w-0 truncate">{t('closeMonth.reopenHistoryItem', { month: label, count: meta.count ?? 0 })}</span>
            <span className="shrink-0">{new Date(e.createdAt).toLocaleString('vi-VN')}</span>
          </li>
        );
      })}
    </ul>
  );
}

// THI-170 partial reopen: replaces the old "reopen the whole month" confirm
// with a picker fed by GET /reopen-preview — one row per reopen SELECTION
// UNIT (an invoice, listing every payer/child it currently covers; or a
// single payer for a pure-credit period with no invoice at all — see
// ReopenPreviewRow's doc comment), ticked by default (= today's full reopen),
// with non-reopenable rows disabled and their reason shown as muted text.
// State (`excluded`) lives in this component, not the parent, and resets
// every time the dialog opens fresh via the `key` on ReopenPickerBody below —
// same mount-reset trick as RecordPaymentDrawer's `formKey`.
function ReopenPickerDialog({
  open, month, onOpenChange, onReopened,
}: {
  open: boolean;
  month: string;
  onOpenChange: (open: boolean) => void;
  /** Fired once the reopen mutation succeeds, before the dialog closes. */
  onReopened: () => void;
}) {
  const { t } = useTranslation('invoicing');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('closeMonth.reopenPicker.title')}</DialogTitle>
          <DialogDescription>{t('closeMonth.reopenPicker.description', { month })}</DialogDescription>
        </DialogHeader>
        <ReopenPickerBody
          key={open ? month : 'closed'}
          month={month}
          onOpenChange={onOpenChange}
          onReopened={onReopened}
        />
      </DialogContent>
    </Dialog>
  );
}

function ReopenPickerBody({
  month, onOpenChange, onReopened,
}: {
  month: string;
  onOpenChange: (open: boolean) => void;
  onReopened: () => void;
}) {
  const { t } = useTranslation('invoicing');
  const { data, isLoading } = useReopenPreview(month);
  const reopen = useReopenMonth();
  const rows = data?.rows ?? [];

  // Excluded rather than selected, same reasoning as the generate-invoices
  // picker above: the default is "every reopenable row ticked" (today's full
  // reopen), and the row list arrives asynchronously. Keyed by `row.key`
  // (invoice or credit-payer identity), not by clientId — a payer can appear
  // on more than one row (their own invoice, plus a second invoice from a
  // later re-close), and each row is its own tickable unit.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  // A 409 (INVOICE_NOT_DRAFT / SUCCESSOR_RECONCILED) keeps the dialog open
  // with this inline message and the selection intact (LOW-6) — closing on
  // error threw away exactly the context the teacher needs to adjust the tick.
  const [error, setError] = useState<string | null>(null);

  const reopenableRows = rows.filter((r) => r.reopenable);
  const selectedRows = reopenableRows.filter((r) => !excluded.has(r.key));
  const selectedCount = selectedRows.length;
  // What actually goes over the wire (fix-round 2): row-grained, not payer-
  // grained — an invoice row contributes its own invoiceId; a credit-only row
  // (no invoice at all) contributes its one payer's clientId instead. This is
  // what keeps ticking one of a payer's two invoices from also sweeping in
  // the other unticked one (the second-close scenario) — a payer-grained
  // `clientIds` selection could not tell the two apart.
  const selectedInvoiceIds = selectedRows.filter((r) => r.invoiceId !== null).map((r) => r.invoiceId!);
  const selectedCreditClientIds = selectedRows.filter((r) => r.invoiceId === null).map((r) => r.payers[0]!.clientId);

  // Click-anywhere + shift-range (THI-180). `orderedIds` is every row in
  // rendered order — reopenable or not — so a range's start/end indices
  // match what the teacher actually sees; `disabledIds` marks the
  // non-reopenable ones so a click or a range never selects them (they're
  // simply skipped, never force-selected — the canonical disabled-rows
  // case this hook has to get right). Controlled the same way as the
  // generate-invoices picker above: toggle/range a derived selected-set,
  // translate the result back into `excluded`.
  const orderedRowKeys = rows.map((r) => r.key);
  const disabledRowKeys = new Set(rows.filter((r) => !r.reopenable).map((r) => r.key));
  const selectedRowKeys = new Set(selectedRows.map((r) => r.key));
  const multiRows = useMultiSelect(
    orderedRowKeys,
    selectedRowKeys,
    (next) => setExcluded(new Set(rows.filter((r) => r.reopenable && !next.has(r.key)).map((r) => r.key))),
    { disabledIds: disabledRowKeys },
  );

  // The three block reasons the design calls for: a RECONCILED successor period
  // always wins (mirrors reopenMonth's own guard precedence), then the two most
  // common non-DRAFT statuses get their own copy — PENDING ("sent to client,
  // awaiting payment" per the schema; there is no separate SENT status) and
  // PAID — with a generic fallback for the rest (PARTIAL/OVERDUE/CANCELLED)
  // that reopenMonth's INVOICE_NOT_DRAFT guard also blocks on.
  const blockedLabel = (row: ReopenPreviewRow): string => {
    if (row.blockReason === 'SUCCESSOR_RECONCILED') return t('closeMonth.reopenPicker.blockedSuccessor');
    if (row.invoiceStatus === 'PENDING') return t('closeMonth.reopenPicker.blockedPending');
    if (row.invoiceStatus === 'PAID') return t('closeMonth.reopenPicker.blockedPaid');
    return t('closeMonth.reopenPicker.blockedOther');
  };

  const onConfirm = () => {
    setError(null);
    reopen.mutate(
      { month, invoiceIds: selectedInvoiceIds, creditClientIds: selectedCreditClientIds },
      {
        onSuccess: () => {
          onReopened();
          onOpenChange(false);
          toast.success(t('closeMonth.reopenSuccess', { month }));
        },
        // useReopenMonth's own onError already toasts the server message
        // (including the localized REOPEN_BLOCKED text); this additionally
        // keeps the dialog open with that same message shown inline, since
        // the teacher's next move is to adjust the tick, not start over.
        onError: (err) => setError(err.message || t('closeMonth.reopenPicker.genericError')),
      },
    );
  };

  return (
    <>
      <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
        <li>{t('closeMonth.reopenPicker.warningDraftsLost')}</li>
        <li>{t('closeMonth.reopenPicker.warningPerPayer')}</li>
        <li>{t('closeMonth.reopenPicker.warningRecloseSelected')}</li>
      </ul>

      {isLoading ? (
        <div className="space-y-2 py-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('closeMonth.reopenPicker.empty')}</p>
      ) : (
        <ul className={cn('max-h-80 space-y-1.5 overflow-y-auto py-1', multiRows.containerProps.className)}>
          {rows.map((row) => {
            const names = row.payers.map((p) => p.clientName).join(', ');
            return (
              <li
                key={row.key}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-md border border-surface-200 px-3 py-2',
                  !row.reopenable && 'opacity-60',
                )}
                {...multiRows.getRowProps(row.key)}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  {/* stopPropagation: the row itself now toggles on click
                      (THI-180) while active — without this the checkbox
                      would flip the row twice. Disabled rows never reach
                      this: getRowProps is a no-op on them, and the Checkbox
                      itself is `disabled`. */}
                  <span onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={multiRows.isSelected(row.key)}
                      disabled={!row.reopenable}
                      onCheckedChange={() => multiRows.toggle(row.key)}
                      aria-label={t('closeMonth.selectPayer', { name: names })}
                    />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{names}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {row.invoiceCode ?? t('closeMonth.reopenPicker.noInvoice')}
                      {!row.reopenable && ` · ${blockedLabel(row)}`}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {row.totalAmount != null && (
                    <span className="text-sm font-semibold tabular-nums">{vnd(row.totalAmount)}</span>
                  )}
                  {row.invoiceStatus && <InvoiceStatusBadge status={row.invoiceStatus} />}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={reopen.isPending}>
          {t('closeMonth.cancel')}
        </Button>
        <BusyButton
          type="button"
          onClick={onConfirm}
          busy={reopen.isPending}
          busyLabel={t('closeMonth.reopenPicker.confirming')}
          disabled={selectedCount === 0}
          className="bg-red-600 hover:bg-red-700"
        >
          {t('closeMonth.reopenPicker.confirm', { count: selectedCount })}
        </BusyButton>
      </DialogFooter>
    </>
  );
}
